import { allocate, divideRounded } from '@fd/money';
import { classifyTransaction } from './classify.ts';
import { bucketIndex, buckets as makeBuckets, type Bucket, type Period } from './periods.ts';
import type { LedgerAccount, LedgerTransaction } from './types.ts';

export type CashFlowTotals = {
  /** Income (positive). */
  readonly incomeMinor: bigint;
  /** Gross spending before refunds (positive). */
  readonly expensesMinor: bigint;
  /** Refunds received (positive). */
  readonly refundsMinor: bigint;
  /** expenses − refunds. */
  readonly netSpendMinor: bigint;
  /** income − net spend. */
  readonly netMinor: bigint;
  /** Money moved into savings/investment assets minus money taken out. */
  readonly savingsContributionMinor: bigint;
};

export type CashFlowBucket = Bucket & CashFlowTotals;

export type CashFlowSummary = {
  readonly currency: string;
  readonly period: Period;
  readonly booked: CashFlowTotals;
  /** Pending bank transactions, shown separately and never mixed into booked totals. */
  readonly pending: CashFlowTotals;
  readonly buckets: readonly CashFlowBucket[];
  /** Net spend per category (null = uncategorised), largest first. */
  readonly byCategory: readonly { readonly categoryId: string | null; readonly netSpendMinor: bigint }[];
  /** Income per category (null = uncategorised), largest first. */
  readonly incomeByCategory: readonly { readonly categoryId: string | null; readonly incomeMinor: bigint }[];
  /** Income per category and the account it came into (whose pay it is), largest first. */
  readonly incomeByAccount: readonly { readonly categoryId: string | null; readonly accountId: string; readonly incomeMinor: bigint }[];
  /** Booked savings contributions per savings/investment account they went to (net of withdrawals), largest first. */
  readonly savingsByAccount: readonly { readonly accountId: string; readonly contributionMinor: bigint }[];
  /**
   * What each account kept of the period: its own change (everything in and out, also
   * settlements, transfers and pending payments), never more than its balance at the period's
   * end; negative where it paid out more. Cards, loans and mortgages keep nothing (they owe); a
   * savings account only its interest.
   */
  readonly stayedByAccount: readonly { readonly accountId: string; readonly stayedMinor: bigint }[];
  readonly transactionCount: number;
  /** Foreign-currency transactions without a base amount yet (left out, listed for review). */
  readonly missingBaseAmount: readonly string[];
};

type Mutable = {
  income: bigint;
  expenses: bigint;
  refunds: bigint;
  contribution: bigint;
};

const empty = (): Mutable => ({ income: 0n, expenses: 0n, refunds: 0n, contribution: 0n });

/** Accounts that only owe: money never "stays" on them. */
const OWING: ReadonlySet<string> = new Set(['CREDIT_CARD', 'LOAN', 'MORTGAGE', 'LIABILITY']);

function freeze(acc: Mutable): CashFlowTotals {
  const netSpend = acc.expenses - acc.refunds;
  return {
    incomeMinor: acc.income,
    expensesMinor: acc.expenses,
    refundsMinor: acc.refunds,
    netSpendMinor: netSpend,
    netMinor: acc.income - netSpend,
    savingsContributionMinor: acc.contribution,
  };
}

/** Amount in base currency: the original when it already is, else the stored conversion. */
export function baseAmount(transaction: LedgerTransaction, baseCurrency: string): bigint | null {
  if (transaction.currency === baseCurrency) return transaction.amountMinor;
  return transaction.baseAmountMinor;
}

/** Category parts of a transaction in base currency (splits get their own categories). */
function categoryParts(
  transaction: LedgerTransaction,
  amount: bigint,
): { categoryId: string | null; amount: bigint }[] {
  if (transaction.splits.length === 0) return [{ categoryId: transaction.categoryId, amount }];
  const weights = transaction.splits.map((s) => (s.amountMinor < 0n ? -s.amountMinor : s.amountMinor));
  if (weights.every((w) => w === 0n)) return [{ categoryId: transaction.categoryId, amount }];
  const parts = allocate(amount, weights, { tieBreakSeed: transaction.id });
  return transaction.splits.map((split, index) => ({ categoryId: split.categoryId, amount: parts[index] as bigint }));
}

/**
 * Income vs spending for a period in the household base currency. Own transfers and
 * settlement payments are excluded; refunds reduce spending instead of counting as income.
 */
export function summarizeCashFlow(
  transactions: readonly LedgerTransaction[],
  accounts: ReadonlyMap<string, LedgerAccount>,
  period: Period,
  baseCurrency: string,
): CashFlowSummary {
  const bucketList = makeBuckets(period);
  const bucketTotals = bucketList.map(empty);
  const booked = empty();
  const pending = empty();
  const categories = new Map<string | null, bigint>();
  const incomeCategories = new Map<string | null, bigint>();
  const incomeAccounts = new Map<string, { categoryId: string | null; accountId: string; incomeMinor: bigint }>();
  const savingsAccounts = new Map<string, bigint>();
  // What each account really kept (the owner, 30.09.2026: the diagram said £220.68 stayed on the
  // main account, which held £50): every movement on an account that holds money — also
  // settlements, repayments of a card, transfers to accounts outside the app, balance corrections,
  // and payments still pending at the bank (that money is leaving already; the owner asked) — so
  // it is the account's own change over the period. A card, loan or mortgage only owes. A
  // savings or investment account keeps only its interest and costs: what came to it from a
  // current account stands among the savings already.
  const kept = new Map<string, bigint>();
  // What came or went after the period, to know each balance at its end (today's without these).
  const later = new Map<string, bigint>();
  const missing: string[] = [];
  let count = 0;

  for (const transaction of transactions) {
    if (transaction.bookingDate > period.end) {
      if (transaction.status === 'BOOKED' || transaction.status === 'PENDING') later.set(transaction.accountId, (later.get(transaction.accountId) ?? 0n) + transaction.amountMinor);
      continue;
    }
    if (transaction.bookingDate < period.start) continue;
    const flow = classifyTransaction(transaction, accounts);
    const holder = accounts.get(transaction.accountId);
    if ((transaction.status === 'BOOKED' || transaction.status === 'PENDING') && flow !== 'IGNORED' && holder && !OWING.has(holder.type)) {
      const moved = baseAmount(transaction, baseCurrency);
      const own = !holder.isSavingsAsset || flow === 'INCOME' || flow === 'EXPENSE' || flow === 'REFUND';
      if (own && moved !== null) kept.set(holder.id, (kept.get(holder.id) ?? 0n) + moved);
    }
    if (flow === 'IGNORED' || flow === 'TRANSFER' || flow === 'SETTLEMENT' || flow === 'ADJUSTMENT') continue;

    const amount = baseAmount(transaction, baseCurrency);
    if (amount === null) {
      missing.push(transaction.id);
      continue;
    }
    count += 1;
    const isPending = transaction.status === 'PENDING';
    const target = isPending ? pending : booked;
    const bucket = isPending ? null : bucketTotals[bucketIndex(bucketList, transaction.bookingDate)];

    const apply = (acc: Mutable) => {
      switch (flow) {
        case 'INCOME':
          acc.income += amount;
          break;
        case 'EXPENSE':
          acc.expenses -= amount;
          break;
        case 'REFUND':
          acc.refunds += amount;
          break;
        case 'SAVINGS_CONTRIBUTION':
        case 'SAVINGS_WITHDRAWAL':
          acc.contribution -= amount;
          break;
      }
    };
    apply(target);
    if (bucket) apply(bucket);

    if (!isPending && flow === 'INCOME') {
      incomeCategories.set(transaction.categoryId, (incomeCategories.get(transaction.categoryId) ?? 0n) + amount);
      const key = `${transaction.categoryId ?? ''}|${transaction.accountId}`;
      const entry = incomeAccounts.get(key) ?? { categoryId: transaction.categoryId, accountId: transaction.accountId, incomeMinor: 0n };
      entry.incomeMinor += amount;
      incomeAccounts.set(key, entry);
    }
    // The account on the other side of the transfer is the savings or investment one.
    if (!isPending && (flow === 'SAVINGS_CONTRIBUTION' || flow === 'SAVINGS_WITHDRAWAL') && transaction.counterpartAccountId) {
      savingsAccounts.set(transaction.counterpartAccountId, (savingsAccounts.get(transaction.counterpartAccountId) ?? 0n) - amount);
    }
    if (!isPending && (flow === 'EXPENSE' || flow === 'REFUND')) {
      for (const part of categoryParts(transaction, amount)) {
        // spending is positive; refunds (positive amounts) reduce it
        categories.set(part.categoryId, (categories.get(part.categoryId) ?? 0n) - part.amount);
      }
    }
  }

  const byCategory = [...categories.entries()]
    .map(([categoryId, netSpendMinor]) => ({ categoryId, netSpendMinor }))
    .sort((a, b) =>
      a.netSpendMinor !== b.netSpendMinor
        ? a.netSpendMinor > b.netSpendMinor
          ? -1
          : 1
        : String(a.categoryId).localeCompare(String(b.categoryId)),
    );

  const incomeByCategory = [...incomeCategories.entries()]
    .map(([categoryId, incomeMinor]) => ({ categoryId, incomeMinor }))
    .sort((a, b) => (a.incomeMinor !== b.incomeMinor ? (a.incomeMinor > b.incomeMinor ? -1 : 1) : String(a.categoryId).localeCompare(String(b.categoryId))));

  return {
    currency: baseCurrency,
    period,
    booked: freeze(booked),
    pending: freeze(pending),
    buckets: bucketList.map((bucket, index) => ({ ...bucket, ...freeze(bucketTotals[index]!) })),
    byCategory,
    incomeByCategory,
    incomeByAccount: [...incomeAccounts.values()].sort((a, b) =>
      a.incomeMinor !== b.incomeMinor ? (a.incomeMinor > b.incomeMinor ? -1 : 1) : `${a.categoryId}|${a.accountId}`.localeCompare(`${b.categoryId}|${b.accountId}`),
    ),
    savingsByAccount: [...savingsAccounts.entries()]
      .map(([accountId, contributionMinor]) => ({ accountId, contributionMinor }))
      .sort((a, b) => (a.contributionMinor !== b.contributionMinor ? (a.contributionMinor > b.contributionMinor ? -1 : 1) : a.accountId.localeCompare(b.accountId))),
    stayedByAccount: [...kept.entries()]
      .map(([accountId, keptMinor]) => {
        // Never more than the account held at the end of the period (its balance then; an account
        // in another currency than the base one is taken as it is).
        const account = accounts.get(accountId)!;
        const end = account.currency === baseCurrency ? account.balanceMinor - (later.get(accountId) ?? 0n) : null;
        const cap = end === null ? keptMinor : end < 0n ? 0n : end;
        return { accountId, stayedMinor: keptMinor < cap ? keptMinor : cap };
      })
      .sort((a, b) => (a.stayedMinor !== b.stayedMinor ? (a.stayedMinor > b.stayedMinor ? -1 : 1) : a.accountId.localeCompare(b.accountId))),
    transactionCount: count,
    missingBaseAmount: missing,
  };
}

/**
 * Savings rate in basis points (10000 = 100%). Default definition (spec §54A):
 * savings contributions / income. The alternative (income − net spend) / income is
 * available for users who prefer it. Null when there was no income.
 */
export function savingsRateBasisPoints(
  totals: CashFlowTotals,
  definition: 'CONTRIBUTIONS' | 'CASH_FLOW' = 'CONTRIBUTIONS',
): bigint | null {
  if (totals.incomeMinor <= 0n) return null;
  const saved = definition === 'CONTRIBUTIONS' ? totals.savingsContributionMinor : totals.netMinor;
  return divideRounded(saved * 10_000n, totals.incomeMinor, 'HALF_UP');
}
